import React, { useEffect, useState } from 'react';
import { Box, Title, Badge } from '@mantine/core';

interface AnimatedTextProps {
  children: string;
  variant?: 'gradient' | 'glow' | 'typewriter' | 'pulse' | 'selectToGradient' | 'complexSequence';
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
  size?: string;
  fw?: number;
  c?: string;
  order?: 1 | 2 | 3 | 4 | 5 | 6;
  ta?: 'left' | 'center' | 'right';
  mb?: string;
}

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  children,
  variant = 'gradient',
  delay = 0,
  className = '',
  style = {},
  size,
  fw,
  c,
  order,
  ta,
  mb
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [showSelection, setShowSelection] = useState(false);
  const [showGradient, setShowGradient] = useState(false);
  const [phase, setPhase] = useState(0); // 0: typing scroll, 1: newline+cursor, 2: typing subtitle, 3: highlight scroll, 4: gradient
  const [line1Text, setLine1Text] = useState('');
  const [line2Text, setLine2Text] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
        const timer = setTimeout(() => {
      setIsVisible(true);
      
      if (variant === 'typewriter') {
        let index = 0;
        const typeInterval = setInterval(() => {
          if (index <= children.length) {
            setCurrentText(children.slice(0, index));
            index++;
          } else {
            clearInterval(typeInterval);
          }
        }, 100);
        
        return () => clearInterval(typeInterval);
      } else if (variant === 'selectToGradient') {
        // Set the full text immediately
        setCurrentText(children);
        
        // Start the typing animation
        let index = 0;
        const typeInterval = setInterval(() => {
          if (index <= children.length) {
            setCurrentText(children.slice(0, index));
            index++;
          } else {
            clearInterval(typeInterval);
            // After typing is complete, show selection effect
            setTimeout(() => {
              setShowSelection(true);
              // After selection, apply gradient
              setTimeout(() => {
                setShowSelection(false);
                setShowGradient(true);
              }, 1500); // Selection duration
            }, 300); // Pause before selection
          }
        }, 80);
        
        return () => clearInterval(typeInterval);
      } else if (variant === 'complexSequence') {
        console.log('Starting complex sequence animation');
        // Reset all states first
        setLine1Text('');
        setLine2Text('');
        setPhase(0);
        setShowSelection(false);
        setShowGradient(false);
        setShowCursor(true);
        
        // Complex sequence: scroll -> newline -> Full-stack engineer -> highlight -> gradient
        const line1 = 'scroll';
        const line2 = 'Full-stack engineer';
        
        let index = 0;
        const phase1Interval = setInterval(() => {
          if (index <= line1.length) {
            const currentText = line1.slice(0, index);
            console.log('Typing:', currentText);
            setLine1Text(currentText);
            index++;
          } else {
            clearInterval(phase1Interval);
            setPhase(1);
            
            // Phase 1: Show newline with cursor (pause)
            setTimeout(() => {
              setPhase(2);
              
              // Phase 2: Type "Full-stack engineer"
              let index2 = 0;
              const phase2Interval = setInterval(() => {
                if (index2 <= line2.length) {
                  setLine2Text(line2.slice(0, index2));
                  index2++;
                } else {
                  clearInterval(phase2Interval);
                  
                  // Phase 3: Highlight "scroll" (pause)
                  setTimeout(() => {
                    setPhase(3);
                    setShowSelection(true);
                    setShowCursor(false); // Hide cursor during selection
                    
                    // Phase 4: Apply gradient to "scroll"
                    setTimeout(() => {
                      setPhase(4);
                      setShowSelection(false);
                      setShowGradient(true);
                      // Cursor stays hidden after gradient is applied
                    }, 1500); // Selection duration
                  }, 300); // Pause after typing
                }
              }, 80);
            }, 800); // Pause at newline
          }
        }, 80);
        
        return () => {
          clearInterval(phase1Interval);
        };
      } else {
        setCurrentText(children);
      }
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [children, delay, variant]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'gradient':
        return {
          background: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c, #4facfe, #00f2fe)',
          backgroundSize: '400% 400%',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'gradientFlow 4s ease-in-out infinite',
          textShadow: 'none'
        };
      case 'glow':
        return {
          color: '#ffffff',
          textShadow: `
            0 0 5px rgba(102, 126, 234, 0.8),
            0 0 10px rgba(102, 126, 234, 0.6),
            0 0 20px rgba(102, 126, 234, 0.4),
            0 0 40px rgba(102, 126, 234, 0.2)
          `,
          animation: 'textGlow 2s ease-in-out infinite alternate'
        };
      case 'typewriter':
        return {
          color: c || '#ffffff',
          borderRight: isVisible ? '3px solid rgba(102, 126, 234, 0.8)' : 'none',
          animation: isVisible ? 'blink 1s infinite' : 'none',
          whiteSpace: 'nowrap' as const
        };
      case 'pulse':
        return {
          color: c || '#ffffff',
          animation: 'textPulse 2s ease-in-out infinite'
        };
      case 'selectToGradient':
        if (showGradient) {
          return {
            background: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c, #4facfe, #00f2fe)',
            backgroundSize: '400% 400%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'gradientFlow 4s ease-in-out infinite',
            textShadow: 'none'
          };
        } else if (showSelection) {
          return {
            background: 'rgba(0, 123, 255, 0.3)',
            color: '#ffffff',
            textShadow: '0 0 10px rgba(0, 123, 255, 0.5)',
            animation: 'selectionPulse 0.5s ease-in-out infinite alternate'
          };
        } else {
          return {
            color: c || '#ffffff',
            borderRight: isVisible && currentText === children ? '3px solid rgba(102, 126, 234, 0.8)' : 'none'
          };
        }
      case 'complexSequence':
        return {
          color: c || '#ffffff',
          whiteSpace: 'pre-line' as const,
          textAlign: 'center' as const
        };
      default:
        return {};
    }
  };

  const baseStyle = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: variant === 'complexSequence' ? 'opacity 0.3s ease' : 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    transitionDelay: variant === 'complexSequence' ? '0s' : `${delay}s`,
    ...getVariantStyles(),
    ...style
  };

  if (order) {
    return (
      <Title
        order={order}
        size={size}
        fw={fw}
        ta={ta}
        mb={mb}
        className={className}
        style={baseStyle}
      >
        {variant === 'complexSequence' ? (
          <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
            <div style={{ 
              display: 'inline',
              background: phase === 3 && showSelection ? 'rgba(0, 123, 255, 0.3)' : 'transparent',
              ...(phase >= 4 && showGradient ? {
                background: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c, #4facfe, #00f2fe)',
                backgroundSize: '400% 400%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradientFlow 4s ease-in-out infinite'
              } : {})
            }}>
              {line1Text}
            </div>
            {phase >= 1 && (
              <>
                <br />
                <div style={{ 
                  display: 'inline',
                  fontSize: '0.5em',
                  fontWeight: 500
                }}>
                  {line2Text}
                </div>
              </>
            )}
            {showCursor && (phase < 4) && (
              <span style={{ 
                animation: 'blink 1s infinite',
                marginLeft: phase === 1 ? '0' : '2px',
                fontSize: phase === 2 ? '0.5em' : '1em' // Smaller cursor for second line
              }}>|</span>
            )}
          </div>
        ) : (variant === 'typewriter' || variant === 'selectToGradient') ? currentText : children}
        {variant === 'typewriter' && isVisible && currentText === children && (
          <span style={{ animation: 'blink 1s infinite' }}>|</span>
        )}
        {variant === 'selectToGradient' && isVisible && currentText === children && !showSelection && !showGradient && (
          <span style={{ animation: 'blink 1s infinite' }}>|</span>
        )}
      </Title>
    );
  }

  return (
    <Box className={className} style={baseStyle}>
      {variant === 'complexSequence' ? (
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
          <div style={{ 
            display: 'inline',
            background: phase === 3 && showSelection ? 'rgba(0, 123, 255, 0.3)' : 'transparent',
            ...(phase >= 4 && showGradient ? {
              background: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #f5576c, #4facfe, #00f2fe)',
              backgroundSize: '400% 400%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'gradientFlow 4s ease-in-out infinite'
            } : {})
          }}>
            {line1Text}
          </div>
          {phase >= 1 && (
                           <>
                 <br />
                 <div style={{ 
                   display: 'inline',
                   fontSize: '0.5em',
                   fontWeight: 500
                 }}>
                   {line2Text}
                 </div>
               </>
             )}
          {showCursor && (phase < 4) && (
            <span style={{ 
              animation: 'blink 1s infinite',
              marginLeft: phase === 1 ? '0' : '2px',
              fontSize: phase === 2 ? '0.5em' : '1em' // Smaller cursor for second line
            }}>|</span>
          )}
        </div>
      ) : (variant === 'typewriter' || variant === 'selectToGradient') ? currentText : children}
      {variant === 'typewriter' && isVisible && currentText === children && (
        <span style={{ animation: 'blink 1s infinite' }}>|</span>
      )}
      {variant === 'selectToGradient' && isVisible && currentText === children && !showSelection && !showGradient && (
        <span style={{ animation: 'blink 1s infinite' }}>|</span>
      )}
    </Box>
  );
};

// Animated Badge component
interface AnimatedBadgeProps {
  children: string;
  delay?: number;
  variant?: 'float' | 'glow' | 'pulse';
  [key: string]: any;
}

export const AnimatedBadge: React.FC<AnimatedBadgeProps> = ({
  children,
  delay = 0,
  variant = 'float',
  ...badgeProps
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'float':
        return {
          animation: 'floatBadge 3s ease-in-out infinite'
        };
      case 'glow':
        return {
          background: 'rgba(102, 126, 234, 0.2)',
          boxShadow: '0 0 20px rgba(102, 126, 234, 0.5)',
          animation: 'badgeGlow 2s ease-in-out infinite alternate'
        };
      case 'pulse':
        return {
          animation: 'badgePulse 2s ease-in-out infinite'
        };
      default:
        return {};
    }
  };

  return (
    <Badge
      {...badgeProps}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)',
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        transitionDelay: `${delay}s`,
        ...getVariantStyles(),
        ...badgeProps.style
      }}
    >
      {children}
    </Badge>
  );
};

// CSS animations
export const AnimatedTextStyles = () => (
  <style>{`
    @keyframes gradientFlow {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    
    @keyframes textGlow {
      0% { 
        text-shadow: 
          0 0 5px rgba(102, 126, 234, 0.8),
          0 0 10px rgba(102, 126, 234, 0.6),
          0 0 20px rgba(102, 126, 234, 0.4);
      }
      100% { 
        text-shadow: 
          0 0 10px rgba(102, 126, 234, 1),
          0 0 20px rgba(102, 126, 234, 0.8),
          0 0 40px rgba(102, 126, 234, 0.6),
          0 0 80px rgba(102, 126, 234, 0.4);
      }
    }
    
    @keyframes textPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.02); }
    }
    
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0; }
    }
    
    @keyframes floatBadge {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      33% { transform: translateY(-5px) rotate(1deg); }
      66% { transform: translateY(2px) rotate(-1deg); }
    }
    
    @keyframes badgeGlow {
      0% { box-shadow: 0 0 15px rgba(102, 126, 234, 0.3); }
      100% { box-shadow: 0 0 25px rgba(102, 126, 234, 0.7); }
    }
    
    @keyframes badgePulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    
    @keyframes selectionPulse {
      0% { 
        background: rgba(0, 123, 255, 0.2);
        box-shadow: 0 0 0px rgba(0, 123, 255, 0.3);
      }
      100% { 
        background: rgba(0, 123, 255, 0.4);
        box-shadow: 0 0 20px rgba(0, 123, 255, 0.6);
      }
    }
  `}</style>
);

export default AnimatedText; 